import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Grid,
  Box,
  Typography,
  Divider,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import { AppDispatch, RootState } from "../../stores";
import { getUserAndSubscriptionMetrics } from "../../features/dashboard-module/dashboardAction";
import { getRecurringRevenueAction } from "../../features/subscription-module/subscriptionAction";

interface ChangeIndicatorProps {
  change: string;
}

const StyledDashboardCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 2.5),
  borderRadius: 1.5,
  backgroundColor: "#f5f0e7",
  transition: "all 0.3s ease-in-out",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  "&:hover": {
    transform: "translateY(-2px)",
    backgroundColor: "#dbd7ce",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5, 2),
  },
  [theme.breakpoints.down("xs")]: {
    padding: theme.spacing(1, 1.5),
  },
  position: "relative",
}));

const ChangeIndicator = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "change",
})<ChangeIndicatorProps>(({ theme, change }) => ({
  marginTop: theme.spacing(2),
  fontWeight: 500,
  fontSize: "0.95rem",
  color: "#536060",
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.75rem",
  },
}));

// Loader Container
const LoaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(4),
  minHeight: "200px",
  width: "100%",
}));

const LoaderText = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(2),
  fontSize: "0.9rem",
  color: "#4b5b5d",
  fontWeight: 500,
}));

const StatsCard: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    metrics,
    loading: dashboardLoading,
    error: dashboardError,
  } = useSelector((state: RootState) => state.dashboard);
  const { recurringRevenue, recurringRevenueLoading, recurringRevenueError } =
    useSelector((state: RootState) => state.subscription);

  const [currentMetricIndex, setCurrentMetricIndex] = useState(0);

  const revenueMetrics = [
    { name: "Monthly Recurring Revenue", key: "mrr", change: "MRR" },
    { name: "Annual Recurring Revenue", key: "arr", change: "ARR" },
  ];

  useEffect(() => {
    dispatch(getUserAndSubscriptionMetrics());
    dispatch(getRecurringRevenueAction());
  }, [dispatch]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentMetricIndex(
        (prevIndex) => (prevIndex + 1) % revenueMetrics.length
      );
    }, 3000);

    return () => clearInterval(interval);
  }, [revenueMetrics.length]);

  const loading = dashboardLoading || recurringRevenueLoading;
  const error = dashboardError || recurringRevenueError;

  if (loading) {
    return (
      <LoaderContainer>
        <CircularProgress
          size={40}
          thickness={4}
          sx={{
            color: "#4b5b5d",
          }}
        />
        <LoaderText>Loading dashboard statistics...</LoaderText>
      </LoaderContainer>
    );
  }

  if (error) {
    return (
      <Typography color="error" sx={{ my: 2, textAlign: "center" }}>
        {error}
      </Typography>
    );
  }

  const statsData = [
    {
      title: "Total Active Users",
      value: metrics?.totalActiveUsers?.toString() || "0",
      change: "Users currently active",
    },
    {
      title: "New Sign-Ups (This Month)",
      value: metrics?.totalNewSignUpUser?.toString() || "0",
      change: "New users this month",
    },
    {
      title: "Churn Rate",
      value: metrics?.churnRate || "0.00%",
      change: "Percentage of canceled subscriptions",
    },
    {
      title: revenueMetrics[currentMetricIndex].name,
      value:
        recurringRevenue?.[
          revenueMetrics[currentMetricIndex].key as "mrr" | "arr"
        ]
          ?.toFixed(2)
          .toString() || "$0",
      change: "Reccuring Revenue",
    },
  ];

  return (
    <Grid
      container
      spacing={{ xs: 2, sm: 2, md: 3 }}
      sx={{ mb: { xs: 2, md: 4 } }}
    >
      {statsData.map((stat, index) => (
        <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
          <StyledDashboardCard>
            <Box
              sx={{
                position: "absolute",
                top: 15,
                right: 15,
                backgroundColor: "#4b5b5d",
                borderRadius: "50%",
                p: "2px 6px",
              }}
            >
              <ArrowOutwardIcon
                sx={{
                  color: "#dbd7ce",
                  fontSize: { xs: "1rem", sm: "1.2rem" },
                }}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "row", sm: "row" },
                alignItems: "center",
                gap: { xs: 1.5, sm: 2 },
                flexGrow: 1,
              }}
            >
              <Box sx={{ flex: 1, overflow: "hidden" }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 500,
                    fontSize: { xs: "0.95rem", sm: "1rem", md: "1.1rem" },
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    color: "#536060",
                  }}
                >
                  {stat.title}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    mt: { xs: 0.5, sm: 1 },
                    mb: 0.5,
                    fontWeight: 700,
                    fontSize: { xs: "1.25rem", sm: "1.4rem", md: "1.6rem" },
                    color: "#536060",
                  }}
                >
                  {stat.value}
                </Typography>
                <Divider />
                <ChangeIndicator change={stat.change}>
                  {stat.change}
                </ChangeIndicator>
              </Box>
            </Box>
          </StyledDashboardCard>
        </Grid>
      ))}
    </Grid>
  );
};

export default StatsCard;
